/**
 * Attribution capture — Master Funnel spec §4.
 *
 * Runs once on first page load per session, extracts every marketing param
 * from the URL, and stores them in ``sessionStorage`` so they persist across
 * navigations. First-touch vs last-touch is kept in ``localStorage``.
 *
 * Consumers call ``getAttribution()`` right before submitting a lead — the
 * returned object is spread into the POST body so every lead in Mongo
 * carries its full provenance.
 *
 * Zero PII stored. Only campaign / channel / creative identifiers.
 */

const SESSION_KEY = "rmc_attribution";
const FIRST_TOUCH_KEY = "rmc_first_touch";
const SESSION_ID_KEY = "rmc_session_id";

const TRACKED_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "gclid", "fbclid", "campaign_id", "adset_id", "ad_id", "ad_creative",
  "keyword",
];

function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function inferSource(url, referrer) {
  try {
    const params = new URLSearchParams(url.search);
    if (params.get("utm_source")) return params.get("utm_source");
    if (params.get("gclid")) return "google-ads";
    if (params.get("fbclid")) return "meta-ads";
    if (!referrer) return "direct";
    const ref = new URL(referrer);
    if (ref.host === url.host) return null; // internal, ignore
    if (ref.host.includes("google.")) return "google-organic";
    if (ref.host.includes("bing.")) return "bing-organic";
    if (ref.host.includes("facebook.") || ref.host.includes("instagram.")) return "meta-organic";
    return ref.host;
  } catch {
    return "direct";
  }
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readFirstTouch() {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(data) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch { /* full/blocked storage — ignore */ }
}

function writeFirstTouch(data) {
  try {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

function ensureSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = newSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, sid);
    }
    return sid;
  } catch {
    return newSessionId();
  }
}

/**
 * Idempotent — safe to call from every route change. Captures fresh
 * marketing params from the current URL and merges them onto the session
 * attribution snapshot (last-touch wins). Records first-touch on first
 * ever visit and never overwrites it.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const referrer = document.referrer || "";
  const sessionId = ensureSessionId();

  const fresh = {};
  for (const key of TRACKED_PARAMS) {
    const v = params.get(key);
    if (v) fresh[key] = v;
  }

  const inferred = inferSource(url, referrer);
  const existing = readSession() || {};

  const merged = {
    ...existing,
    ...fresh,
    session_id: sessionId,
    landing_page: existing.landing_page || url.pathname + url.search,
    referrer: existing.referrer || referrer || null,
    last_touch_source: fresh.utm_source || inferred || existing.last_touch_source || "direct",
    updated_at: new Date().toISOString(),
  };
  writeSession(merged);

  const first = readFirstTouch();
  if (!first) {
    writeFirstTouch({
      utm_source: fresh.utm_source || null,
      utm_medium: fresh.utm_medium || null,
      utm_campaign: fresh.utm_campaign || null,
      gclid: fresh.gclid || null,
      fbclid: fresh.fbclid || null,
      first_touch_source: fresh.utm_source || inferred || "direct",
      first_touch_page: url.pathname + url.search,
      first_touch_referrer: referrer || null,
      first_touch_at: new Date().toISOString(),
    });
  }
}

/**
 * Returns a flat object ready to spread into a lead POST body. Combines
 * session (last-touch) attribution with the persisted first-touch data
 * plus any campaign entry_intent stashed by a CampaignLandingPage.
 */
export function getAttribution() {
  const s = readSession() || {};
  const f = readFirstTouch() || {};
  let entryIntent = null;
  try { entryIntent = sessionStorage.getItem("rmc_entry_intent"); } catch { /* noop */ }
  return {
    utm_source: s.utm_source || null,
    utm_medium: s.utm_medium || null,
    utm_campaign: s.utm_campaign || null,
    utm_content: s.utm_content || null,
    utm_term: s.utm_term || null,
    gclid: s.gclid || null,
    fbclid: s.fbclid || null,
    campaign_id: s.campaign_id || null,
    adset_id: s.adset_id || null,
    ad_id: s.ad_id || null,
    ad_creative: s.ad_creative || null,
    keyword: s.keyword || null,
    landing_page: s.landing_page || null,
    referrer: s.referrer || null,
    session_id: s.session_id || null,
    last_touch_source: s.last_touch_source || null,
    first_touch_source: f.first_touch_source || null,
    entry_intent: entryIntent || null,
  };
}
